import { formatAturanPakai, formatNamaRacikan } from "@/utils/aturanPakai";

export const SIGNA_LAIN = "lain";

export const DEPO_HD = "08";

export interface RefSigna {
  id: number;
  nama: string;
  kode?: string | null;
  label_signa?: string | null;
  label_interval?: string | null;
  kali_per_hari?: number | null;
  is_prn?: boolean | number | null;
  sediaan_group?: string[];
  disabled?: boolean;
}

export interface RefRuteResep {
  id: number;
  nama: string;
  kata_kerja?: string | null;
  disabled?: boolean;
}

export interface RefSatuanDosis {
  id: number;
  kode: string;
  nama: string;
  tipe: string;
  is_multi_dose: boolean;
  disabled?: boolean;
}

export interface RefAturanTambahan {
  id: number;
  kode: string;
  label_etiket: string;
  disabled?: boolean;
}

export interface BarisResepTerstruktur {
  id: number | null;
  obat_id: string | null;
  obat_nama: string | null;
  qty: number | string | null;
  qty_unit: string | null;
  harga: number | null;
  frekuensi_id: number | null;
  rute_id: number | null;
  dosis_qty: number | string | null;
  satuan_dosis_id: number | null;
  signa_bebas: string | null;
  aturan_tambahan_ids: number[];
  sediaan_group: string | null;
  alasan_melebihi_jkn: string | null;
  alasan_aptol: string | null;
  _aptol: CekAptol | null;
  _maks_jkn: number | null;
  _restriksi: string | null;
  _signa_lain: boolean;
  _auto_qty: boolean;
  _durasi_hari: number | null;
  _sumber: string | null;
  _is_high_alert: boolean;
  _is_lasa: boolean;
  _is_obat_kronis: boolean;
  _fokus: boolean;
  _dosis_lama: string | null;
  _disalin: string | null;
}

export type IsianAturanPakai = Pick<
  BarisResepTerstruktur,
  | "frekuensi_id"
  | "rute_id"
  | "dosis_qty"
  | "satuan_dosis_id"
  | "signa_bebas"
  | "aturan_tambahan_ids"
  | "_signa_lain"
>;

export interface KomponenRacikanTerstruktur {
  id: number | null;
  obat_id: string | null;
  obat_nama: string | null;
  gnr: string | null;
  dosis_qty: number | string | null;
  satuan_dosis_id: number | null;
  qty: number | string | null;
  qty_unit: string | null;
  _dosis_lama: string | null;
  _kunci: number;
  _fokus: boolean;
}

export interface RacikanTerstruktur {
  id: number | null;
  r: string;
  nama: string;
  sediaan_id: number | null;
  qty: number | string | null;
  frekuensi_id: number | null;
  rute_id: number | null;
  dosis_qty: number | string | null;
  satuan_dosis_id: number | null;
  signa_bebas: string | null;
  aturan_tambahan_ids: number[];
  bud_hari: number | string | null;
  komponen: KomponenRacikanTerstruktur[];
  _signa_lain: boolean;
  _kunci: number;
  _fokus: boolean;
}

export interface KonteksBatasJkn {
  berlaku: boolean;
  penjamin: string | null;
  alasan: string | null;
  cakupan: "register" | "resep";
  override_dokter: boolean;
  alasan_min: number;
  terpakai: Record<string, number>;
}

export type StatusCekAptol =
  | "TIDAK_BERLAKU"
  | "AMAN"
  | "BLOCKED"
  | "UNAVAILABLE";

export interface CekAptol {
  status: StatusCekAptol;
  pesan: string | null;
  perkiraan_habis?: string | null;
}

export interface KonteksAptol {
  aktif: boolean;
  override_dokter: boolean;
  alasan_min: number;
  status: string;
  pesan: string | null;
  fetched_at: string | null;
  cek: CekAptol | null;
}

export interface StatusBatasJkn {
  maks: number;
  terpakai: number;
  sisa: number;
  melebihi: boolean;
}

export interface ReferensiBaris {
  signa: RefSigna[];
  rute: RefRuteResep[];
  satuan: RefSatuanDosis[];
  aturan: RefAturanTambahan[];
}

function angka(nilai: unknown): number | null {
  if (nilai === null || nilai === undefined || nilai === "") {
    return null;
  }
  const hasil = Number(String(nilai).replace(",", "."));
  return isFinite(hasil) ? hasil : null;
}

function idAngka(nilai: unknown): number | null {
  const hasil = angka(nilai);
  return hasil !== null && hasil > 0 ? hasil : null;
}

function dosisPositif(nilai: unknown): number | null {
  const hasil = angka(nilai);
  return hasil !== null && hasil > 0 ? hasil : null;
}

function lebihDariTigaDesimal(nilai: unknown): boolean {
  const hasil = angka(nilai);
  if (hasil === null) {
    return false;
  }
  const seribu = hasil * 1000;
  return Math.abs(seribu - Math.round(seribu)) > 0.000001;
}

export function cocokAlergi(
  alergi: string | null | undefined,
  namaObat: string | null | undefined
): boolean {
  if (!alergi || !namaObat) {
    return false;
  }
  const nama = String(namaObat).toLowerCase();
  return String(alergi)
    .toLowerCase()
    .split(/[,;]+/)
    .map(a => a.trim())
    .some(a => a !== "" && a !== "-" && nama.includes(a));
}

export function barisBaru(
  sumber: Partial<BarisResepTerstruktur> = {}
): BarisResepTerstruktur {
  return {
    id: null,
    obat_id: null,
    obat_nama: null,
    qty: null,
    qty_unit: null,
    harga: null,
    frekuensi_id: null,
    rute_id: null,
    dosis_qty: null,
    satuan_dosis_id: null,
    signa_bebas: null,
    aturan_tambahan_ids: [],
    sediaan_group: null,
    alasan_melebihi_jkn: null,
    alasan_aptol: null,
    _aptol: null,
    _maks_jkn: null,
    _restriksi: null,
    _signa_lain: false,
    _auto_qty: false,
    _durasi_hari: null,
    _sumber: null,
    _is_high_alert: false,
    _is_lasa: false,
    _is_obat_kronis: false,
    _fokus: false,
    _dosis_lama: null,
    _disalin: null,
    ...sumber
  };
}

export function barisDariItemResep(item: any): BarisResepTerstruktur {
  const signaBebas = item.signa_bebas ? String(item.signa_bebas) : null;
  return barisBaru({
    id: item.id ?? null,
    obat_id: item.obat_id ?? null,
    obat_nama: item.obat_nama ?? null,
    qty: item.qty ?? null,
    qty_unit: item.qty_unit ?? null,
    harga: item.harga ?? null,
    frekuensi_id: idAngka(item.frekuensi_id ?? item.frekuensi?.id),
    rute_id: idAngka(item.rute_id ?? item.rute?.id),
    dosis_qty: item.dosis_qty ?? null,
    satuan_dosis_id: idAngka(item.satuan_dosis_id),
    signa_bebas: signaBebas,
    aturan_tambahan_ids: Array.isArray(item.aturan_tambahan_ids)
      ? item.aturan_tambahan_ids.map((id: unknown) => Number(id))
      : [],
    alasan_melebihi_jkn: item.alasan_melebihi_jkn || null,
    alasan_aptol: item.alasan_aptol || null,
    _aptol: item.aptol_status
      ? { status: item.aptol_status as StatusCekAptol, pesan: null }
      : null,
    _maks_jkn: angka(item.qty_max_jkn),
    _signa_lain: !!signaBebas,
    _dosis_lama:
      item.dosis_qty === null || item.dosis_qty === undefined
        ? item.ap_dosis || null
        : null
  });
}

export function angkaTeks(nilai: number): string {
  return String(Math.round(nilai * 100) / 100).replace(".", ",");
}

export function statusBatasJkn(
  baris: BarisResepTerstruktur,
  konteks: KonteksBatasJkn | null,
  terpakaiBarisLain = 0
): StatusBatasJkn | null {
  const maks = angka(baris._maks_jkn);
  if (!konteks || !konteks.berlaku || !baris.obat_id || !maks || maks <= 0) {
    return null;
  }
  const kunci = String(baris.obat_id)
    .trim()
    .toUpperCase();
  const terpakai =
    (Number(konteks.terpakai && konteks.terpakai[kunci]) || 0) +
    terpakaiBarisLain;
  const sisa = Math.max(0, maks - terpakai);
  const qty = angka(baris.qty) || 0;
  return { maks, terpakai, sisa, melebihi: qty > sisa + 0.000001 };
}

export function masalahJkn(
  baris: BarisResepTerstruktur,
  status: StatusBatasJkn | null,
  konteks: KonteksBatasJkn | null
): string | null {
  if (!status || !status.melebihi || !konteks) {
    return null;
  }
  const nama = baris.obat_nama || "Obat";
  if (!konteks.override_dokter) {
    return `${nama}: jumlah melebihi batas JKN, paling banyak ${angkaTeks(
      status.sisa
    )}`;
  }
  const alasan = String(baris.alasan_melebihi_jkn || "").trim();
  if (!alasan) {
    return `${nama}: jumlah melebihi batas JKN ${angkaTeks(
      status.maks
    )} — kurangi jumlah atau tulis alasan klinis`;
  }
  if (alasan.length < konteks.alasan_min) {
    return `${nama}: alasan melebihi batas JKN minimal ${konteks.alasan_min} karakter`;
  }
  return null;
}

export function masalahAptol(
  baris: BarisResepTerstruktur,
  konteks: KonteksAptol | null
): string | null {
  if (
    !konteks ||
    !konteks.aktif ||
    !baris._aptol ||
    baris._aptol.status !== "BLOCKED"
  ) {
    return null;
  }
  const nama = baris.obat_nama || "Obat";
  if (!konteks.override_dokter) {
    return `${nama}: obat pasien masih ada menurut Apotek Online BPJS — hapus atau ganti obat`;
  }
  const alasan = String(baris.alasan_aptol || "").trim();
  if (alasan.length < konteks.alasan_min) {
    return `${nama}: obat masih ada menurut Apotek Online — tulis alasan klinis (minimal ${konteks.alasan_min} karakter)`;
  }
  return null;
}

export function cariSigna(
  ref: ReferensiBaris,
  id: number | null
): RefSigna | undefined {
  return ref.signa.find(s => Number(s.id) === Number(id));
}

export function cariSatuan(
  ref: ReferensiBaris,
  id: number | null
): RefSatuanDosis | undefined {
  return ref.satuan.find(s => Number(s.id) === Number(id));
}

export function labelSigna(signa: RefSigna): string {
  const label = [signa.label_signa, signa.label_interval]
    .filter(bagian => !!bagian && String(bagian).trim() !== "")
    .join(" ");
  const isi = label || signa.nama;
  return signa.kode ? `${signa.kode} · ${isi}` : isi;
}

export function signaUntukSediaan(
  ref: ReferensiBaris,
  sediaanGroup: string | null,
  terpilih: number | null
): RefSigna[] {
  return ref.signa.filter(signa => {
    if (Number(signa.id) === Number(terpilih)) {
      return true;
    }
    if (signa.disabled) {
      return false;
    }
    const grup = signa.sediaan_group || [];
    return !sediaanGroup || grup.length === 0 || grup.includes(sediaanGroup);
  });
}

export function pratinjauAturanPakai(
  baris: IsianAturanPakai,
  ref: ReferensiBaris
): string {
  const signa = baris._signa_lain
    ? undefined
    : cariSigna(ref, baris.frekuensi_id);
  const rute = ref.rute.find(r => Number(r.id) === Number(baris.rute_id));
  const satuan = cariSatuan(ref, baris.satuan_dosis_id);
  const aturan = ref.aturan
    .filter(a => baris.aturan_tambahan_ids.includes(Number(a.id)))
    .map(a => a.label_etiket);

  return formatAturanPakai({
    kata_kerja: rute?.kata_kerja ?? null,
    signa_bebas: baris._signa_lain ? baris.signa_bebas : null,
    label_signa: signa?.label_signa ?? null,
    label_interval: signa?.label_interval ?? null,
    is_prn: signa?.is_prn ?? null,
    dosis_qty: baris.dosis_qty,
    satuan_dosis: satuan?.nama ?? null,
    aturan_tambahan: aturan
  });
}

export function hitungJumlah(
  baris: BarisResepTerstruktur,
  ref: ReferensiBaris
): number | null {
  const signa = cariSigna(ref, baris.frekuensi_id);
  const satuan = cariSatuan(ref, baris.satuan_dosis_id);
  const dosis = angka(baris.dosis_qty);
  const kali = angka(signa?.kali_per_hari);

  if (
    baris._signa_lain ||
    !baris._durasi_hari ||
    !signa ||
    signa.is_prn ||
    !kali ||
    !dosis ||
    !satuan ||
    satuan.is_multi_dose
  ) {
    return null;
  }

  return Math.ceil(kali * dosis * baris._durasi_hari);
}

export function masalahBaris(baris: BarisResepTerstruktur): string | null {
  const nama = baris.obat_nama || "Obat";

  if (!baris.obat_id) {
    return "Salah satu baris belum memilih obat";
  }
  if (!angka(baris.qty) || (angka(baris.qty) as number) <= 0) {
    return `${nama}: jumlah wajib diisi`;
  }
  if (baris._signa_lain) {
    if (!baris.signa_bebas || !String(baris.signa_bebas).trim()) {
      return `${nama}: tulis signa lain-lain`;
    }
    if (dosisPositif(baris.dosis_qty) && !baris.satuan_dosis_id) {
      return `${nama}: pilih satuan dosis atau kosongkan dosis`;
    }
  } else {
    if (!baris.frekuensi_id) {
      return `${nama}: signa wajib dipilih`;
    }
    if (!dosisPositif(baris.dosis_qty) || !baris.satuan_dosis_id) {
      return `${nama}: dosis dan satuan dosis wajib diisi`;
    }
  }
  if (lebihDariTigaDesimal(baris.dosis_qty)) {
    return `${nama}: dosis maksimal 3 angka di belakang koma`;
  }
  if (!baris.rute_id) {
    return `${nama}: rute wajib dipilih`;
  }
  return null;
}

export function barisKePayload(baris: BarisResepTerstruktur) {
  const lain = baris._signa_lain;
  return {
    id: baris.id,
    obat_id: baris.obat_id,
    qty: angka(baris.qty),
    qty_unit: baris.qty_unit,
    rute_id: baris.rute_id,
    frekuensi_id: lain ? null : baris.frekuensi_id,
    signa_bebas: lain ? String(baris.signa_bebas || "").trim() : null,
    dosis_qty: dosisPositif(baris.dosis_qty),
    satuan_dosis_id: dosisPositif(baris.dosis_qty)
      ? baris.satuan_dosis_id
      : null,
    aturan_tambahan_ids: baris.aturan_tambahan_ids,
    alasan_melebihi_jkn:
      String(baris.alasan_melebihi_jkn || "").trim() || null,
    alasan_aptol: String(baris.alasan_aptol || "").trim() || null,
    ap_template_id: null,
    r: ""
  };
}

let kunciRacikan = 0;

export function komponenBaru(
  sumber: Partial<KomponenRacikanTerstruktur> = {}
): KomponenRacikanTerstruktur {
  return {
    id: null,
    obat_id: null,
    obat_nama: null,
    gnr: null,
    dosis_qty: null,
    satuan_dosis_id: null,
    qty: null,
    qty_unit: null,
    _dosis_lama: null,
    _kunci: ++kunciRacikan,
    _fokus: false,
    ...sumber
  };
}

export function racikanBaru(
  sumber: Partial<RacikanTerstruktur> = {}
): RacikanTerstruktur {
  return {
    id: null,
    r: "",
    nama: "",
    sediaan_id: null,
    qty: null,
    frekuensi_id: null,
    rute_id: null,
    dosis_qty: null,
    satuan_dosis_id: null,
    signa_bebas: null,
    aturan_tambahan_ids: [],
    bud_hari: null,
    komponen: [],
    _signa_lain: false,
    _kunci: ++kunciRacikan,
    _fokus: false,
    ...sumber
  };
}

export function racikanDariResep(racikan: any): RacikanTerstruktur {
  const signaBebas = racikan.signa_bebas ? String(racikan.signa_bebas) : null;
  return racikanBaru({
    id: racikan.id ?? null,
    r: racikan.r || "",
    nama: racikan.nama || "",
    sediaan_id: idAngka(racikan.sediaan_id ?? racikan.sediaan?.id),
    qty: racikan.qty ?? null,
    frekuensi_id: idAngka(racikan.frekuensi_id ?? racikan.frekuensi?.id),
    rute_id: idAngka(racikan.rute_id ?? racikan.rute?.id),
    dosis_qty: racikan.dosis_qty ?? null,
    satuan_dosis_id: idAngka(racikan.satuan_dosis_id),
    signa_bebas: signaBebas,
    aturan_tambahan_ids: Array.isArray(racikan.aturan_tambahan_ids)
      ? racikan.aturan_tambahan_ids.map((id: unknown) => Number(id))
      : [],
    bud_hari: racikan.bud_hari ?? null,
    komponen: (racikan.items || [])
      .filter((item: any) => !item.stopped_at)
      .map((item: any) =>
        komponenBaru({
          id: item.id ?? null,
          obat_id: item.obat_id ?? null,
          obat_nama: item.obat_nama ?? null,
          dosis_qty: item.dosis_qty ?? null,
          satuan_dosis_id: idAngka(item.satuan_dosis_id),
          qty: item.qty ?? null,
          qty_unit: item.qty_unit ?? null,
          _dosis_lama:
            item.dosis_qty === null || item.dosis_qty === undefined
              ? item.ap_dosis || null
              : null
        })
      ),
    _signa_lain: !!signaBebas
  });
}

export function nomorRacikanBerikut(daftar: RacikanTerstruktur[]): number {
  return (
    daftar.reduce((maks, racikan) => {
      const cocok = /(\d+)\s*$/.exec(racikan.r || "");
      return cocok ? Math.max(maks, Number(cocok[1])) : maks;
    }, 0) + 1
  );
}

export function namaEtiketRacikan(
  racikan: RacikanTerstruktur,
  ref: ReferensiBaris
): string {
  const cocok = /(\d+)\s*$/.exec(racikan.r || "");
  return formatNamaRacikan(
    cocok ? Number(cocok[1]) : 1,
    racikan.komponen.map(k => ({
      nama: k.gnr || k.obat_nama,
      dosis_qty: k.dosis_qty,
      satuan: cariSatuan(ref, k.satuan_dosis_id)?.nama ?? null
    }))
  );
}

export function masalahRacikan(racikan: RacikanTerstruktur): string | null {
  const nama = racikan.nama || "Racikan";

  if (!String(racikan.nama || "").trim()) {
    return "Nama racikan wajib diisi";
  }
  if (!racikan.sediaan_id) {
    return `${nama}: sediaan wajib dipilih`;
  }
  if (!angka(racikan.qty) || (angka(racikan.qty) as number) <= 0) {
    return `${nama}: jumlah wajib diisi`;
  }
  if (racikan._signa_lain) {
    if (!racikan.signa_bebas || !String(racikan.signa_bebas).trim()) {
      return `${nama}: tulis signa lain-lain`;
    }
  } else {
    if (!racikan.frekuensi_id) {
      return `${nama}: signa wajib dipilih`;
    }
    if (!dosisPositif(racikan.dosis_qty) || !racikan.satuan_dosis_id) {
      return `${nama}: dosis dan satuan dosis wajib diisi`;
    }
  }
  if (lebihDariTigaDesimal(racikan.dosis_qty)) {
    return `${nama}: dosis maksimal 3 angka di belakang koma`;
  }
  if (!racikan.rute_id) {
    return `${nama}: rute wajib dipilih`;
  }
  const bud = angka(racikan.bud_hari);
  if (bud !== null && (!Number.isInteger(bud) || bud < 1 || bud > 3650)) {
    return `${nama}: BUD berupa jumlah hari bulat 1–3650`;
  }
  if (racikan.komponen.length === 0) {
    return `${nama}: tambahkan minimal satu komponen obat`;
  }
  const komponenKurang = racikan.komponen.find(
    k => !k.obat_id || !dosisPositif(k.dosis_qty) || !k.satuan_dosis_id
  );
  if (komponenKurang) {
    return `${nama}: dosis & satuan komponen ${komponenKurang.obat_nama ||
      ""} wajib diisi`;
  }
  const komponenPresisi = racikan.komponen.find(k =>
    lebihDariTigaDesimal(k.dosis_qty)
  );
  if (komponenPresisi) {
    return `${nama}: dosis ${komponenPresisi.obat_nama ||
      "komponen"} maksimal 3 angka di belakang koma — gunakan satuan lebih kecil (mis. mcg)`;
  }
  return null;
}

export function racikanKePayload(racikan: RacikanTerstruktur) {
  const lain = racikan._signa_lain;
  const dosis = dosisPositif(racikan.dosis_qty);
  const bud = angka(racikan.bud_hari);
  return {
    header: {
      id: racikan.id,
      nama: racikan.nama,
      r: racikan.r,
      qty: angka(racikan.qty),
      sediaan_id: racikan.sediaan_id,
      frekuensi_id: lain ? null : racikan.frekuensi_id,
      signa_bebas: lain ? String(racikan.signa_bebas || "").trim() : null,
      rute_id: racikan.rute_id,
      dosis_qty: dosis,
      satuan_dosis_id: dosis ? racikan.satuan_dosis_id : null,
      aturan_tambahan_ids: racikan.aturan_tambahan_ids,
      bud_hari: bud !== null && bud >= 1 ? Math.round(bud) : null
    },
    komponen: racikan.komponen.map(k => ({
      id: k.id,
      obat_id: k.obat_id,
      qty: angka(k.qty),
      qty_unit: k.qty_unit,
      dosis_qty: dosisPositif(k.dosis_qty),
      satuan_dosis_id: k.satuan_dosis_id,
      signa_bebas: null,
      aturan_tambahan_ids: [],
      ap_template_id: null,
      r: racikan.r
    }))
  };
}
